/**
 * Règles pures du module « Requêtes » (problèmes signalés sur un véhicule).
 * Aucun appel réseau ni DOM : testé par Jest (requetesLogique.test.js).
 */

export const PHOTOS_MAX = 3;

/** Destinataires préremplis : l'auteur + la liste des rondes de sécurité (sans doublon). */
export const destinatairesParDefaut = (config, auteurId) => {
  const ids = [auteurId, ...((config && config.destinataires_ids) || [])].filter(Boolean);
  const emails = ((config && config.destinataires_emails) || [])
    .map((e) => String(e || '').trim().toLowerCase())
    .filter((e) => e.includes('@'));
  return { ids: [...new Set(ids)], emails: [...new Set(emails)] };
};

export const TYPES_ACTIF = ['vehicule', 'equipement', 'epi', 'point_eau'];

/** Type, id et nom de l'actif d'une requête (anciennes requêtes : véhicule). */
export const typeActif = (r) => (r && r.actif_type) || 'vehicule';
export const idActif = (r) => (r && (r.actif_id || r.vehicule_id)) || '';
export const nomActif = (r) => (r && (r.actif_nom || r.vehicule_nom)) || '';

/** Le formulaire de création peut-il partir ? */
export const peutEnvoyer = (form) => Boolean(
  form && (form.actif_id || form.vehicule_id) && form.categorie_id && String(form.description || '').trim()
);

/** Fermer ou rouvrir exige un motif ; une modification aussi. */
export const motifValide = (motif) => String(motif || '').trim().length > 0;

export const adresseValide = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim());

/** Ajoute une adresse (minuscules, sans doublon) ; adresse invalide → liste inchangée. */
export const ajouterAdresse = (liste, email) => {
  const e = String(email || '').trim().toLowerCase();
  if (!adresseValide(e) || (liste || []).includes(e)) return liste || [];
  return [...(liste || []), e];
};

const TYPES_VISIBLES = ['creation', 'commentaire', 'photo', 'priorite', 'modification',
  'fermeture', 'reouverture', 'hors_service', 'en_service'];

/** Entrées du fil montrées à l'écran (sans les traces techniques de courriel / rappel). */
export const entreesVisibles = (fil) => (fil || []).filter((e) => TYPES_VISIBLES.includes(e.type));

/** Les N dernières entrées visibles (carte de l'accueil). */
export const derniersAjouts = (fil, n = 2) => entreesVisibles(fil).slice(-n);

export const aDesPhotos = (requete) => Boolean(requete && (requete.nb_photos > 0
  || (requete.fil || []).some((e) => (e.photos || []).length > 0)));

const normaliser = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * Filtres de « Toutes les requêtes ».
 * filtres = { statut, actif_type, actif_id (ou vehicule_id), categorie_id, priorite, source, recherche }
 */
export const filtrerRequetes = (requetes, filtres = {}) => {
  const q = normaliser(filtres.recherche).trim();
  return (requetes || []).filter((r) => {
    if (filtres.statut && r.statut !== filtres.statut) return false;
    const actifFiltre = filtres.actif_id || filtres.vehicule_id;
    if (actifFiltre && idActif(r) !== actifFiltre) return false;
    if (filtres.actif_type && typeActif(r) !== filtres.actif_type) return false;
    if (filtres.categorie_id && r.categorie_id !== filtres.categorie_id) return false;
    if (filtres.priorite && r.priorite !== filtres.priorite) return false;
    if (filtres.source && (r.source || 'manuelle') !== filtres.source) return false;
    if (q) {
      const texte = normaliser([
        r.numero, nomActif(r), r.description, r.cree_par_nom,
        ...(r.fil || []).map((e) => `${e.auteur_nom || ''} ${e.texte || ''}`),
      ].join(' '));
      if (!texte.includes(q)) return false;
    }
    return true;
  });
};

/** `categories` : liste, ou { type d'actif: liste } avec la requête pour choisir le type. */
export const nomCategorie = (categories, id, requete) => {
  const liste = Array.isArray(categories) ? categories : ((categories || {})[typeActif(requete)] || []);
  const c = liste.find((x) => x.id === id);
  return c ? c.nom : (id || '');
};

/** Lignes de l'export Excel (en-têtes traduits par l'appelant). */
export const lignesExport = (requetes, categories, t, formaterDate) => (requetes || []).map((r) => {
  const dernier = entreesVisibles(r.fil).slice(-1)[0];
  return {
    [t('requetes.colonnes.numero')]: r.numero,
    [t('requetes.colonnes.ouverteLe')]: formaterDate(r.created_at),
    [t('requetes.colonnes.ouvertePar')]: r.cree_par_nom || '',
    [t('requetes.colonnes.categorie')]: nomCategorie(categories, r.categorie_id, r),
    [t('requetes.colonnes.type')]: t(`requetes.typeActif.${typeActif(r)}`),
    [t('requetes.colonnes.unite')]: nomActif(r),
    [t('requetes.colonnes.description')]: r.description || '',
    [t('requetes.colonnes.dernierAjout')]: dernier && dernier.type !== 'creation'
      ? `${formaterDate(dernier.date)} — ${dernier.auteur_nom || ''} : ${dernier.texte || ''}` : '',
    [t('requetes.colonnes.cout')]: r.cout ?? '',
    [t('requetes.colonnes.priorite')]: t(`requetes.priorite.${r.priorite}`),
    [t('requetes.colonnes.statut')]: t(`requetes.statut.${r.statut}`),
    [t('requetes.colonnes.source')]: t(`requetes.source.${r.source || 'manuelle'}`),
    [t('requetes.colonnes.miseAJour')]: formaterDate(r.updated_at),
  };
});

/** Changements envoyés par « Modifier » (seulement ce qui a changé). */
export const changementsModification = (avant, apres) => {
  const champs = {};
  ['categorie_id', 'description', 'priorite', 'met_hors_service'].forEach((cle) => {
    if (apres[cle] !== undefined && apres[cle] !== avant[cle]) champs[cle] = apres[cle];
  });
  const coutApres = apres.cout === '' || apres.cout === null || apres.cout === undefined ? null : Number(apres.cout);
  if (coutApres !== (avant.cout ?? null) && !(coutApres === null && avant.cout == null)) champs.cout = coutApres;
  const egal = (a, b) => JSON.stringify([...(a || [])].sort()) === JSON.stringify([...(b || [])].sort());
  if (apres.destinataires_ids && !egal(apres.destinataires_ids, avant.destinataires_ids)) {
    champs.destinataires_ids = apres.destinataires_ids;
  }
  if (apres.destinataires_emails && !egal(apres.destinataires_emails, avant.destinataires_emails)) {
    champs.destinataires_emails = apres.destinataires_emails;
  }
  return champs;
};
